/**
 * The desk's targets - the numbers the overview measures its tiles against.
 *
 * Mirrors apps/api/src/modules/settings/crm-goals.settings.ts. Its own file
 * rather than a corner of `dashboard.ts`, because the two answer to different
 * routes: the overview is one big read that changes on every period tab, and
 * this is a small read-write that changes when somebody decides what the month
 * is for.
 *
 * A target is RECURRING, not a row per month: "every month, 8 000 EUR" stays
 * true until it is changed. The consequence is stated on the dialog itself -
 * there is no history, so changing the monthly target rewrites what past months
 * were measured against.
 */
import { api } from "./api";

export type GoalPeriod = "month" | "quarter" | "year";

export const GOAL_PERIODS: readonly GoalPeriod[] = ["month", "quarter", "year"];

export interface CrmGoalSet {
  /** Payments collected in the window, in cents. Null = no target set. */
  revenueCents: number | null;
  /** Leads created in the window. Null = no target set. */
  leadsNew: number | null;
  /** Leads that reached `won` in the window. Null = no target set. */
  dealsWon: number | null;
}

export type CrmGoals = Record<GoalPeriod, CrmGoalSet>;

export const EMPTY_GOAL_SET: CrmGoalSet = { revenueCents: null, leadsNew: null, dealsWon: null };

export const getGoals = () => api.get<{ goals: CrmGoals }>("/admin/crm/goals");

/** Replace one window's targets. The reply carries all three back. */
export const putGoals = (period: GoalPeriod, goals: CrmGoalSet) =>
  api.put<{ goals: CrmGoals }>(`/admin/crm/goals/${period}`, goals);

/** True when this window has at least one target to draw. */
export function hasAnyGoal(set: CrmGoalSet | null | undefined): boolean {
  if (!set) return false;
  return set.revenueCents !== null || set.leadsNew !== null || set.dealsWon !== null;
}

/**
 * How far along a figure is, as a percentage of its target.
 *
 * Null when there is no target, so the caller draws nothing rather than a bar at
 * zero - "no target" and "nothing achieved yet" look identical on an empty bar
 * and mean opposite things.
 *
 * NOT capped at 100. The bar itself is clamped where it is drawn, but the number
 * is the truth: a month that beat its target by half should be able to say 150,
 * and rounding that down to 100 would hide the best result the desk ever had.
 */
export function goalPct(current: number, target: number | null): number | null {
  if (target === null || target <= 0) return null;
  return Math.round((current / target) * 100);
}

/**
 * A typed euro amount as cents, or null when the field is empty or unusable.
 *
 * Its own function rather than an `onChange` handler in the dialog, because it is
 * the one place where what the operator typed becomes what the database stores,
 * and getting it wrong is a target off by a factor of a hundred.
 *
 * A comma and a full stop are the same character here. Estonian writes 8000,50
 * and the number pad on a laptop produces a full stop, so refusing either would
 * refuse a correct amount for a reason nobody could see. Spaces (including the
 * non-breaking kind a paste from a spreadsheet brings) are group separators and
 * are dropped.
 *
 * Anything left over after that - a currency symbol, a letter, a second
 * separator - makes the whole field null rather than being stripped out. Half of
 * a mistyped number is still a number, and silently storing it is worse than
 * showing the operator that the field did not take.
 */
export function parseGoalEuros(input: string): number | null {
  const cleaned = input.replace(/[\s  ]/g, "").replace(",", ".");
  if (!cleaned) return null;
  if (!/^\d+(\.\d{0,2})?$/.test(cleaned)) return null;
  const cents = Math.round(Number(cleaned) * 100);
  if (!Number.isFinite(cents) || cents <= 0) return null;
  return cents;
}

/**
 * Cents back into the text the field shows.
 *
 * Whole euros lose the decimals: a target is a round intention, and "8000,00" in
 * an input box invites somebody to edit the wrong half of it.
 */
export function formatGoalEuros(cents: number | null): string {
  if (cents === null) return "";
  return cents % 100 === 0 ? String(cents / 100) : (cents / 100).toFixed(2).replace(".", ",");
}

/**
 * A typed count as a whole number, or null.
 *
 * Same contract as the money field on purpose: empty is null, junk is null, and
 * a partially valid string does not become a partially valid target.
 */
export function parseGoalCount(input: string): number | null {
  const cleaned = input.replace(/[\s  ]/g, "");
  if (!cleaned) return null;
  if (!/^\d+$/.test(cleaned)) return null;
  const n = Number(cleaned);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}
